import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  Card,
  CardContent,
  Grid,
  Pagination,
} from "@mui/material";
import { Search, Visibility, Person, Phone, CheckCircle } from "@mui/icons-material";
import { ApiGet } from "../../../../ApiSetup/Api";

/**
 * Simple Reference Tracking Page
 * Shows who referred the most members
 */
const ReferenceTracking = () => {
  const [loading, setLoading] = useState(true);
  const [referrers, setReferrers] = useState([]);
  const [stats, setStats] = useState({ totalUniqueReferrers: 0, totalReferredMembers: 0 });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedReferrer, setSelectedReferrer] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [referredMembers, setReferredMembers] = useState([]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await ApiGet("admin/reference-tracking/stats");
      if (response.data?.status) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  // Fetch top referrers
  const fetchReferrers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (search) params.append("search", search);
      
      const response = await ApiGet(`admin/reference-tracking/top-referrers?${params}`);
      if (response.data?.status) {
        setReferrers(response.data.data.referrers);
        setTotalPages(response.data.data.pagination.totalPages);
      }
    } catch (error) {
      console.error("Error fetching referrers:", error);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  // Fetch referred members for a specific phone
  const fetchReferredMembers = async (phoneNumber) => {
    setDetailsLoading(true);
    try {
      const response = await ApiGet(`admin/reference-tracking/members/${phoneNumber}`);
      if (response.data?.status) {
        setReferredMembers(response.data.data.referredMembers);
        setSelectedReferrer(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching referred members:", error);
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchReferrers();
  }, [fetchReferrers]);

  const handleViewDetails = (referrer) => {
    fetchReferredMembers(referrer.referrerPhone);
    setDetailsOpen(true);
  };

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setPage(1);
      fetchReferrers();
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Reference Tracking
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Track members who referred others (by reference phone number in registration)
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Person sx={{ fontSize: 40, color: "primary.main", mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">
                {stats.totalUniqueReferrers}
              </Typography>
              <Typography color="text.secondary">Total Referrers</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <CheckCircle sx={{ fontSize: 40, color: "success.main", mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">
                {stats.totalReferredMembers}
              </Typography>
              <Typography color="text.secondary">Members Referred</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <TextField
        placeholder="Search by phone number..."
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyPress={handleSearch}
        sx={{ mb: 2, width: 300 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
      />

      {/* Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.100" }}>
                  <TableCell><b>#</b></TableCell>
                  <TableCell><b>Referrer Phone</b></TableCell>
                  <TableCell><b>PPSK Member?</b></TableCell>
                  <TableCell><b>Member Name</b></TableCell>
                  <TableCell align="center"><b>Referral Count</b></TableCell>
                  <TableCell align="center"><b>Action</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {referrers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No referrers found
                    </TableCell>
                  </TableRow>
                ) : (
                  referrers.map((referrer, index) => (
                    <TableRow key={referrer.referrerPhone} hover>
                      <TableCell>{(page - 1) * 15 + index + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Phone fontSize="small" color="action" />
                          {referrer.referrerPhone}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {referrer.isPPSKMember ? (
                          <Chip label="Yes" color="success" size="small" />
                        ) : (
                          <Chip label="No" color="default" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        {referrer.isPPSKMember && referrer.referrerDetails ? (
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {referrer.referrerDetails.fullName || "-"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {referrer.referrerDetails.ppskId || ""}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={referrer.referralCount}
                          color="primary"
                          sx={{ fontWeight: "bold", minWidth: 50 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleViewDetails(referrer)}
                        >
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Phone />
            Referred Members by {selectedReferrer?.referrerPhone}
          </Box>
          {selectedReferrer?.isPPSKMember && selectedReferrer?.referrerDetails && (
            <Typography variant="body2" color="text.secondary">
              Referrer: {selectedReferrer.referrerDetails.fullName} ({selectedReferrer.referrerDetails.ppskId})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {detailsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {referredMembers.length === 0 ? (
                <ListItem>
                  <ListItemText primary="No referred members found" />
                </ListItem>
              ) : (
                referredMembers.map((member, index) => (
                  <React.Fragment key={member._id}>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography fontWeight="medium">
                              {member.fullName || "N/A"}
                            </Typography>
                            <Chip
                              label={member.verificationStatus}
                              size="small"
                              color={
                                member.verificationStatus === "Approved"
                                  ? "success"
                                  : member.verificationStatus === "Pending"
                                  ? "warning"
                                  : "default"
                              }
                            />
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" component="span">
                              {member.phoneNumber} • {member.ppskId || "No PPSK ID"}
                            </Typography>
                            <br />
                            <Typography variant="caption" color="text.secondary">
                              Joined: {new Date(member.createdAt).toLocaleDateString()}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                    {index < referredMembers.length - 1 && <Divider />}
                  </React.Fragment>
                ))
              )}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ReferenceTracking;
